import React, { Component, useState, useEffect, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Button, Icon, Box, Typography, CircularProgress } from "@material-ui/core";
import NisaBankModal from "./NisaBankModal";
import swal from "sweetalert";
import { config } from "../../../constants";
import axios from "axios";

// Session timeout constants
const STANDARD_BANK_SESSION_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const NEDBANK_SESSION_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const FNB_SESSION_TIMEOUT = 10 * 60 * 1000; // 10 minutes

// Standard Bank session helpers
const loadStandardBankSession = (profileId) => {
  const sessionKey = `standardBankSession_${profileId}`;
  const savedSession = localStorage.getItem(sessionKey);
  if (!savedSession) return null;

  try {
    const session = JSON.parse(savedSession);
    if (Date.now() - session.startedAt > STANDARD_BANK_SESSION_TIMEOUT) {
      localStorage.removeItem(sessionKey);
      return null;
    }
    return session;
  } catch (error) {
    localStorage.removeItem(sessionKey);
    return null;
  }
};

const clearStandardBankSession = (profileId) => {
  const sessionKey = `standardBankSession_${profileId}`;
  localStorage.removeItem(sessionKey);
};

// Nedbank session helpers
const loadNedbankSession = (profileId) => {
  const sessionKey = `nedbankSession_${profileId}`;
  const savedSession = localStorage.getItem(sessionKey);
  if (!savedSession) return null;

  try {
    const session = JSON.parse(savedSession);
    if (Date.now() - session.startedAt > NEDBANK_SESSION_TIMEOUT) {
      localStorage.removeItem(sessionKey);
      return null;
    }
    return session;
  } catch (error) {
    localStorage.removeItem(sessionKey);
    return null;
  }
};

const clearNedbankSession = (profileId) => {
  const sessionKey = `nedbankSession_${profileId}`;
  localStorage.removeItem(sessionKey);
};

// FNB session helpers
const loadFnbSession = (profileId) => {
  const sessionKey = `fnbSession_${profileId}`;
  const savedSession = localStorage.getItem(sessionKey);
  if (!savedSession) return null;

  try {
    const session = JSON.parse(savedSession);
    if (Date.now() - session.startedAt > FNB_SESSION_TIMEOUT) {
      localStorage.removeItem(sessionKey);
      return null;
    }
    return session;
  } catch (error) {
    localStorage.removeItem(sessionKey);
    return null;
  }
};

const clearFnbSession = (profileId) => {
  const sessionKey = `fnbSession_${profileId}`;
  localStorage.removeItem(sessionKey);
};

// Get active bank session
const getActiveBankSession = (profileId) => {
  const standardBankSession = loadStandardBankSession(profileId);
  const nedbankSession = loadNedbankSession(profileId);
  const fnbSession = loadFnbSession(profileId);

  if (standardBankSession && !["approved", "error", "failed"].includes(standardBankSession.status)) {
    return { bank: "Standard Bank", session: standardBankSession };
  }
  if (nedbankSession && !["approved", "error", "failed"].includes(nedbankSession.status)) {
    return { bank: "Nedbank", session: nedbankSession };
  }
  if (fnbSession && !["approved", "failed"].includes(fnbSession.status)) {
    return { bank: "FNB Bank", session: fnbSession };
  }

  return null;
};

const NisaBankVerification = (props) => {
    const { title, actionName, img } = props;
    const [showModal, setShowModal] = useState(false);
    const [activeSession, setActiveSession] = useState(null);
    const [autoOpenStatusModal, setAutoOpenStatusModal] = useState(null);
    const stopPollingRef = useRef(null);

    const handleShow = () => {
        setShowModal(true);
    };

    const handleHide = () => {
        setShowModal(false);
        // Reset auto-open state when modal closes
        setAutoOpenStatusModal(null);
    };

    // Get profile ID from localStorage
    const getProfileId = () => {
      try {
        const user = localStorage.getItem("usr");
        return JSON.parse(user)?.profile;
      } catch (error) {
        console.error("Error getting profile:", error);
        return null;
      }
    };

    // Check for active sessions
    useEffect(() => {
      const checkActiveSessions = () => {
        const profileId = getProfileId();
        if (!profileId) return;

        const active = getActiveBankSession(profileId);
        setActiveSession(active);
      };

      checkActiveSessions();

      // Check every 2 seconds
      const interval = setInterval(checkActiveSessions, 2000);

      return () => clearInterval(interval);
    }, []);

    // Handle cancel session
    const handleCancelSession = async (bankName) => {
      console.log('[DEBUG] handleCancelSession called with bankName:', bankName);

      const confirmed = await swal({
        title: "Cancel Session?",
        text: `Are you sure you want to cancel the ${bankName} session?`,
        icon: "warning",
        buttons: ["No, keep it", "Yes, cancel"],
        dangerMode: true,
      });

      if (!confirmed) {
        console.log('[DEBUG] User cancelled confirmation');
        return;
      }

      try {
        const profileId = getProfileId();
        console.log('[DEBUG] profileId:', profileId);

        if (!profileId) {
          console.error('[DEBUG] profileId is null/undefined, cannot clear session');
          swal("Error", "Unable to identify user profile. Please refresh the page.", "error");
          return;
        }

        // Stop polling FIRST (most important - stops API calls immediately)
        console.log('[DEBUG] Stopping polling for bank:', bankName);
        if (stopPollingRef.current) {
          stopPollingRef.current();
          console.log('[DEBUG] Polling stopped via ref');
        }

        // Clear localStorage SECOND (even if API fails later)
        console.log('[DEBUG] Clearing localStorage for bank:', bankName);
        if (bankName === "Standard Bank") {
          clearStandardBankSession(profileId);
          // Verify it's cleared
          const check = localStorage.getItem(`standardBankSession_${profileId}`);
          console.log('[DEBUG] After clear, Standard Bank session exists?', check !== null, 'value:', check);
        } else if (bankName === "Nedbank") {
          clearNedbankSession(profileId);
          const check = localStorage.getItem(`nedbankSession_${profileId}`);
          console.log('[DEBUG] After clear, Nedbank session exists?', check !== null, 'value:', check);
        } else if (bankName === "FNB Bank") {
          clearFnbSession(profileId);
          const check = localStorage.getItem(`fnbSession_${profileId}`);
          console.log('[DEBUG] After clear, FNB session exists?', check !== null, 'value:', check);
        }

        // Clear active session state to hide pill immediately
        console.log('[DEBUG] Setting activeSession to null');
        setActiveSession(null);

        // THEN call reset API (if this fails, localStorage is already cleared)
        let endpoint = "";
        if (bankName === "Standard Bank") {
          endpoint = `${config.BANK_API_URL}/api/standardBank/resetStatus`;
        } else if (bankName === "Nedbank") {
          endpoint = `${config.BANK_API_URL}/api/nedbank/resetStatus`;
        } else if (bankName === "FNB Bank") {
          endpoint = `${config.BANK_API_URL}/api/fnbBank/resetStatus`;
        }

        if (endpoint) {
          console.log('[DEBUG] Calling reset API:', endpoint);
          try {
            await axios.post(endpoint, { profileId: profileId });
            console.log('[DEBUG] Reset API call successful');
          } catch (apiError) {
            console.error('[DEBUG] API call failed but localStorage already cleared:', apiError);
            // Don't throw error - localStorage is already cleared
          }
        }

        swal("Session Cancelled", "You can now start a new session.", "success");
      } catch (error) {
        console.error("[DEBUG] Error cancelling session:", error);
        swal("Error", "Failed to cancel session. Please try again.", "error");
      }
    };

    return (
      <>
        {/* Session In Progress Pill - Show before the button */}
        {activeSession && (
          <Row className="mt-3">
            <Col md={{ span: 9, offset: 0 }}>
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 20px",
                  marginBottom: "16px",
                  backgroundColor: "#fff3cd",
                  border: "1px solid #ffc107",
                  borderRadius: "20px",
                  transition: "background-color 0.2s",
                }}
              >
                {/* Left side - Clickable to view progress */}
                <Box
                  onClick={() => {
                    // Open the respective STATUS modal based on which bank has active session
                    // DON'T open the bank selection modal - status modals are independent Material-UI modals
                    if (activeSession.bank === "Standard Bank") {
                      setAutoOpenStatusModal("standardBank");
                    } else if (activeSession.bank === "Nedbank") {
                      setAutoOpenStatusModal("nedbank");
                    } else if (activeSession.bank === "FNB Bank") {
                      setAutoOpenStatusModal("fnb");
                    }
                    // Don't call setShowModal(true) - we only want status modal, not bank selection modal
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    flex: 1,
                  }}
                >
                  <CircularProgress size={16} style={{ marginRight: 8, color: "#856404" }} />
                  <Typography variant="body2" style={{ color: "#856404", fontWeight: 500 }}>
                    {activeSession.bank} session in progress - Click to view
                  </Typography>
                </Box>

                {/* Right side - Cancel & Retry button */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelSession(activeSession.bank);
                  }}
                  style={{
                    marginLeft: 16,
                    borderColor: "#856404",
                    color: "#856404",
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  Cancel & Retry
                </Button>
              </Box>
            </Col>
          </Row>
        )}

        {/* Add Bank Button Row */}
        <Row className="mt-3 align-items-center">
          <Col md={{ span: 4 }}>{title}</Col>
          <Col md={{ span: 5 }} className="d-flex align-items-center">
            <>
              <div className="w-50 text-center" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '46px', gap: '8px' }}>
                <img src={img.fnb} alt="FNB Bank" style={{ maxHeight: "35px", objectFit: "contain" }} />
                <img src={img.standard} alt="Standard Bank" style={{ maxHeight: "28px", objectFit: "contain" }} />
                <img src={img.nedbank} alt="Nedbank" style={{ maxHeight: "50px", objectFit: "contain" }} />
              </div>
              <Button
                color="primary"
                onClick={handleShow}
              >
                <Icon color="primary">{actionName}</Icon>
              </Button>
              <NisaBankModal
                show={showModal}
                onHide={handleHide}
                autoOpenStatusModal={autoOpenStatusModal}
                onStatusModalOpened={() => setAutoOpenStatusModal(null)}
                onStopPolling={stopPollingRef}
              />
            </>
          </Col>
        </Row>
      </>
    );
  }

export default NisaBankVerification; 

